import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";

import {
  DpExpiringProbation,
  listExpiringProbationsApi,
  PROBATION_MILESTONE_LABELS,
} from "../api/contractsApi";
import { formatDateOnly } from "../utils/format";
import { DP_EMPLOYEE_TAB_PAGE, DP_PAGE, DP_PROCESS_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

const daysLabel = (days: number) => (days === 0 ? "hoje" : days === 1 ? "amanhã" : `em ${days} dias`);

/** O que o DP decide em cada marco. */
const DECISION: Record<DpExpiringProbation["milestone"], string> = {
  PRIMEIRO_PERIODO: "Decidir se prorroga",
  FIM: "Decidir se efetiva ou encerra",
};

/**
 * Contratos de experiência com o fim do 1º período ou o fim da experiência nos
 * próximos dias (janela da Administração do sistema). Depois do fim, o contrato
 * segue por prazo indeterminado sozinho — por isso a lista é só do que vem pela frente.
 */
export default function ExpiringProbationsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [days, setDays] = useState<number | null>(null);
  const [items, setItems] = useState<DpExpiringProbation[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    listExpiringProbationsApi(token)
      .then((result) => {
        setDays(result.days);
        setItems(result.items);
      })
      .catch((error: any) => toast.error(error?.message || "Erro ao carregar as experiências."))
      .finally(() => setLoading(false));
  }, [token]);

  if (!token) return null;

  const open = (item: DpExpiringProbation) =>
    item.terminationProcessId
      ? navigate(DP_PROCESS_PAGE(item.terminationProcessId))
      : navigate(DP_EMPLOYEE_TAB_PAGE(item.employee.id, "contrato"));

  const situation = (item: DpExpiringProbation) =>
    item.terminationProcessId ? (
      <span className="dpBadge dpBadge--gray">Desligamento em andamento</span>
    ) : (
      <span className={`dpBadge ${item.daysLeft <= 3 ? "dpBadge--red" : "dpBadge--orange"}`}>
        {DECISION[item.milestone]}
      </span>
    );

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[
              { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
              { label: "Experiências terminando" },
            ]}
            title="Experiências terminando"
            subtitle={
              days
                ? `Fim do 1º período ou da experiência nos próximos ${days} dias. Depois do fim, o contrato segue por prazo indeterminado.`
                : "Fim do 1º período ou da experiência nos próximos dias."
            }
            onBack={goBack}
            backLabel="Voltar para o DP"
          />

          <section className="ecoCard dpeList" aria-label="Experiências">
            <div className="dpeList__toolbar">
              <span className="dpeList__count" aria-live="polite">
                {loading ? "Carregando..." : `${items.length} ${items.length === 1 ? "contrato" : "contratos"}`}
              </span>
            </div>

            {!loading && items.length === 0 ? (
              <div className="dpeList__empty">
                <p className="ecoEmpty">Nenhuma experiência terminando nesta janela.</p>
              </div>
            ) : (
              <>
                <div className="ecoTableWrap dpeList__table">
                  <table className="ecoTable">
                    <thead>
                      <tr>
                        <th scope="col">Colaborador</th>
                        <th scope="col">Loja</th>
                        <th scope="col">Admissão</th>
                        <th scope="col">Marco</th>
                        <th scope="col">Data</th>
                        <th scope="col">Situação</th>
                        <th scope="col" className="ecoTable__num">Ação</th>
                      </tr>
                    </thead>
                    <tbody aria-busy={loading}>
                      {items.map((item) => (
                        <tr key={item.contractId}>
                          <td className="dpePerson__name">{item.employee.fullName}</td>
                          <td className="ecoTable__muted">{item.store?.nomeFantasia ?? "—"}</td>
                          <td className="ecoTable__muted">{formatDateOnly(item.admissionDate)}</td>
                          <td>{PROBATION_MILESTONE_LABELS[item.milestone]}</td>
                          <td>
                            {formatDateOnly(item.date)}{" "}
                            <span className="ecoTable__muted">({daysLabel(item.daysLeft)})</span>
                          </td>
                          <td>{situation(item)}</td>
                          <td className="ecoTable__num">
                            <button
                              type="button"
                              className="ecoBtn ecoBtn--sm"
                              onClick={() => open(item)}
                              aria-label={`Abrir ${item.employee.fullName}`}
                            >
                              {item.terminationProcessId ? "Abrir desligamento" : "Abrir na ficha"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <ul className="dpeList__cards" aria-busy={loading}>
                  {items.map((item) => (
                    <li key={item.contractId}>
                      <button type="button" className="dpeList__card" onClick={() => open(item)}>
                        <span className="dpePerson__text" style={{ flex: 1 }}>
                          <span className="dpePerson__name">{item.employee.fullName}</span>
                          <span className="dpePerson__sub">
                            {PROBATION_MILESTONE_LABELS[item.milestone]} em {formatDateOnly(item.date)} (
                            {daysLabel(item.daysLeft)})
                          </span>
                        </span>
                        {situation(item)}
                        <EcoIcon name="chevronRight" className="dpeList__cardChevron" />
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
